import FlashcardCompactCard from "@/components/flashcards/FlashcardCompactCard";
import { cn } from "@/lib/utils";
import type { TextFlashcardData } from "@/types/storyboard";

const STUDY_COLUMN_COUNT = 3;
/** Cell height matches a full 3×3 — `1rem` = two `gap-2` gutters between three rows. */
const STUDY_FIT_ROW_COUNT = 3;
/** Six tracks so each card spans two — leftover last-row cards can start centered. */
const FIT_GRID_TRACK_COUNT = STUDY_COLUMN_COUNT * 2;
const FIT_CARD_TRACK_SPAN = 2;

const FIT_CONTAINER_LIST_CLASS_NAME = [
  "grid grid-cols-1 gap-2 content-center",
  "md:h-full md:min-h-0 md:grid-cols-6",
  "md:auto-rows-[minmax(0,calc((100%-1rem)/3))]",
] as const;

const INLINE_LIST_CLASS_NAME =
  "mx-auto flex max-w-5xl flex-wrap justify-center";

const FIT_ITEM_CLASS_NAME = "box-border min-h-0 md:col-span-2";
const INLINE_ITEM_CLASS_NAME =
  "box-border w-full basis-full p-2 md:w-1/3 md:basis-1/3";

const FIT_COL_START_CLASS_NAME: Record<number, string> = {
  2: "md:col-start-2",
  3: "md:col-start-3",
  4: "md:col-start-4",
};

interface FlashcardsCardGridProps {
  cards: TextFlashcardData[];
  flippedByIndex: Record<number, boolean>;
  onFlip: (index: number) => void;
  /**
   * Size cards as a fixed 3×3 in the picker study pane. Fewer than three rows
   * stay that cell size and are centered vertically. Inline lesson grids keep
   * aspect-ratio height.
   */
  fitContainer?: boolean;
  className?: string;
}

const filledRowCount = (cardCount: number) =>
  Math.max(1, Math.ceil(cardCount / STUDY_COLUMN_COUNT));

const lastRowCardCount = (cardCount: number) => {
  const remainder = cardCount % STUDY_COLUMN_COUNT;
  if (remainder === 0) {
    return STUDY_COLUMN_COUNT;
  }
  return remainder;
};

/**
 * 1-based grid-column start for a card in the fit layout. Incomplete last rows
 * are offset so one or two leftovers sit centered under a full three-card row.
 */
export const fitGridColumnStart = (
  index: number,
  cardCount: number
): number | undefined => {
  const leftovers = cardCount % STUDY_COLUMN_COUNT;
  if (leftovers === 0) {
    return undefined;
  }

  const lastRowStartIndex = cardCount - leftovers;
  if (index < lastRowStartIndex) {
    return undefined;
  }

  const positionInLastRow = index - lastRowStartIndex;
  const leadingEmptyTracks =
    (FIT_GRID_TRACK_COUNT - leftovers * FIT_CARD_TRACK_SPAN) / 2;

  return leadingEmptyTracks + 1 + positionInLastRow * FIT_CARD_TRACK_SPAN;
};

const fitGridColumnStartClassName = (index: number, cardCount: number) => {
  const columnStart = fitGridColumnStart(index, cardCount);
  if (!columnStart) {
    return undefined;
  }
  return FIT_COL_START_CLASS_NAME[columnStart];
};

const listClassName = (fitContainer: boolean, className?: string) => {
  if (fitContainer) {
    return cn(FIT_CONTAINER_LIST_CLASS_NAME, className);
  }
  return cn(INLINE_LIST_CLASS_NAME, className);
};

const itemClassName = (
  fitContainer: boolean,
  index: number,
  cardCount: number
) => {
  if (!fitContainer) {
    return INLINE_ITEM_CLASS_NAME;
  }
  return cn(FIT_ITEM_CLASS_NAME, fitGridColumnStartClassName(index, cardCount));
};

const fitStudyMeta = (fitContainer: boolean, cardCount: number) => {
  if (!fitContainer) {
    return {
      studyRowSize: undefined,
      filledRows: undefined,
      lastRowCount: undefined,
    };
  }

  return {
    studyRowSize: STUDY_FIT_ROW_COUNT,
    filledRows: filledRowCount(cardCount),
    lastRowCount: lastRowCardCount(cardCount),
  };
};

/**
 * Three-column wrap/center flashcard grid — used for in-lesson cards and the
 * study modal so every card stays independently flippable.
 */
const FlashcardsCardGrid = ({
  cards,
  flippedByIndex,
  onFlip,
  fitContainer = false,
  className,
}: FlashcardsCardGridProps) => {
  const studyMeta = fitStudyMeta(fitContainer, cards.length);

  return (
    <ul
      aria-label="Lernkarten"
      data-study-row-size={studyMeta.studyRowSize}
      data-filled-rows={studyMeta.filledRows}
      data-last-row-count={studyMeta.lastRowCount}
      className={listClassName(fitContainer, className)}
    >
      {cards.map((card, index) => (
        <li
          key={index}
          className={itemClassName(fitContainer, index, cards.length)}
        >
          <FlashcardCompactCard
            cardNumber={index + 1}
            front={card.frontside}
            back={card.backside}
            isFlipped={Boolean(flippedByIndex[index])}
            onFlip={() => onFlip(index)}
            fitHeight={fitContainer}
          />
        </li>
      ))}
    </ul>
  );
};

export default FlashcardsCardGrid;
